import find from 'lodash/find';
import moment from 'moment';

import constants from 'pages/open-course/common/constants';

import type { FixedLatePenalty, IncrementalLatePenalty } from 'bundles/author-common/constants/GradingLatePenalty';
import { FIXED, INCREMENTAL } from 'bundles/author-common/constants/GradingLatePenalty';
import type {
  GradableStoredContent,
  LectureContent,
  QuizTypeContent,
  StaffGradedContent,
  StoredContent,
} from 'bundles/author-common/types/Item';
import ChangeScopeTypes from 'bundles/author-course/constants/ChangeScopeTypes';
import DefaultTimeCommitments from 'bundles/author-course/constants/DefaultTimeCommitments';
import type { ItemTypeType } from 'bundles/author-course/constants/ItemTypes';
import ItemTypes, { DIALOGUE } from 'bundles/author-course/constants/ItemTypes';
import type { CustomDisplayTypenameOverride, ItemLockingScope, ItemScope } from 'bundles/author-course/types/types';
import { getAssignmentDraftItemProperties, getAssignmentItemType } from 'bundles/author-course/utils/ItemTypeUtils';
import type { WriteAccessState } from 'bundles/authoring/common/constants';
import { getCoachSpecificItemFromItem, isWidgetItemADialogue } from 'bundles/coach-item/common/lib';
import { COACH_ITEM_SHARED_ITEM_TYPENAME } from 'bundles/item/constants/constants';
import type { ItemType, ItemTypeLabel } from 'bundles/item/types/ItemType';

type Drafts = Record<
  string,
  {
    draftName: string;
    itemTypeLabel: ItemTypeLabel;
  }
>;

type LatePenalty = FixedLatePenalty | IncrementalLatePenalty;
type ItemContext = {
  changeScope?: ItemScope[] | ItemLockingScope[];
  isBreakingChangeAllowed?: boolean;
  isBranchPrivate?: boolean;
  itemLocks?: {
    lockingItemId: Record<string, string>;
  };
  branchId: string;
  courseId: string;
};

type ConstructorParams = {
  context: ItemContext;
  moduleId: string;
  lessonId: string;
  id: string;
  atomId: string;
  name: string;
  originalName?: string;
  content: StoredContent;
  published: boolean;
  lockableByItem: string;
  isLockable: boolean;
  isCustomContent: boolean;
  timeCommitmentOverride: number;
  isHonors: boolean;
  embeddedContentSourceCourseId?: string;
  customDisplayTypenameOverride?: CustomDisplayTypenameOverride;
  generationSource: GenerationSource;
  drafts: Drafts;
  writeAccessState?: WriteAccessState;
};

type GenerationSource = 'AI' | 'USER';

// TODO: Typing for this model is not finished.
class Item {
  declare id: string;

  declare context: ItemContext;

  declare moduleId: string;

  declare lessonId: string;

  declare atomId: string;

  declare content: StoredContent;

  declare typeName: ItemType;

  declare definition: StoredContent['definition'];

  declare published: boolean;

  declare lockableByItem?: string;

  declare isLockable: boolean;

  declare isCustomContent: boolean;

  declare timeCommitmentOverride?: number;

  declare standardProctorConfigurationId?: string;

  declare embeddedContentSourceCourseId?: string;

  declare isUpdated: boolean;

  declare draftName?: string;

  declare draftItemTypeLabel?: ItemTypeLabel;

  declare writeAccessState?: WriteAccessState;

  declare generationSource: GenerationSource;

  /**
   * Override for the display typename of this item. Used currently for coach items
   * where a single typename ('coach') can represent multiple display types ('dialogue', 'rolePlay').
   * Expected values: 'Dialogue' | 'Role Play' | undefined
   */
  declare customDisplayTypenameOverride?: CustomDisplayTypenameOverride;

  constructor({
    context = {} as ItemContext,
    moduleId,
    lessonId,
    id,
    atomId,
    name,
    originalName,
    content,
    published,
    lockableByItem,
    isLockable,
    isCustomContent,
    timeCommitmentOverride,
    isHonors,
    embeddedContentSourceCourseId,
    customDisplayTypenameOverride,
    generationSource,
    drafts,
    writeAccessState,
  }: ConstructorParams) {
    this.context = context;
    this.moduleId = moduleId;
    this.lessonId = lessonId;
    this.generationSource = generationSource;

    this.id = id;
    this.atomId = atomId;
    this._name = name;
    this._originalName = name === originalName ? undefined : originalName; // we only care about originalName if it is different than name
    this.content = content;
    this.typeName = content.typeName;
    this.definition = content.definition;

    this.published = published;
    this.lockableByItem = lockableByItem;
    this.isLockable = isLockable;
    this.isCustomContent = isCustomContent;
    this.timeCommitmentOverride = timeCommitmentOverride;
    this.standardProctorConfigurationId =
      content.definition && (content as StaffGradedContent).definition.standardProctorConfigurationId;
    this._isHonors = isHonors;
    this.embeddedContentSourceCourseId = embeddedContentSourceCourseId;
    this.writeAccessState = writeAccessState;
    this.customDisplayTypenameOverride = customDisplayTypenameOverride;

    const { changeScope } = context;

    this.isUpdated =
      !changeScope ||
      changeScope.some((scope: ItemScope | ItemLockingScope) => {
        return this.checkIfUpdated(scope);
      });

    if (drafts) {
      const draft = drafts[id];

      this.draftName = draft?.draftName;
      this.draftItemTypeLabel = draft?.itemTypeLabel;
    }
  }

  declare _name: string;

  get name() {
    return this.draftName || this._name || this.defaultName;
  }

  declare _originalName?: string;

  get originalName() {
    return this._originalName;
  }

  declare _isHonors: boolean;

  get isHonors(): boolean {
    return this._isHonors;
  }

  get itemType(): ItemTypeType {
    if (this.typeName === 'staffGraded' || this.typeName === 'ungradedAssignment') {
      const draftItemProperties = getAssignmentDraftItemProperties(this.draftItemTypeLabel);

      return getAssignmentItemType(
        draftItemProperties?.isTeamSubmissionEnabled ??
          (this.content as StaffGradedContent)?.definition?.isTeamSubmissionEnabled,
        draftItemProperties?.draftTypeName || this.typeName
      );
    }

    if (isWidgetItemADialogue(this)) {
      return DIALOGUE;
    }

    if (this.typeName === COACH_ITEM_SHARED_ITEM_TYPENAME) {
      return getCoachSpecificItemFromItem(this);
    }
    // @ts-expect-error TODO: It can possibly return undefined
    return find(ItemTypes, (type) => type.typeName === this.typeName);
  }

  get typeLabel() {
    return (this.itemType && this.itemType.label) || this.typeName;
  }

  get routeName() {
    return this.itemType && this.itemType.routeName;
  }

  get publishedName() {
    return this._name;
  }

  get defaultName() {
    return this.itemType && this.itemType.defaultName;
  }

  get prefix() {
    return this.itemType && this.itemType.prefix;
  }

  get translatedPrefix() {
    if (this.typeName === 'staffGraded' || this.typeName === 'ungradedAssignment') {
      const draftItemProperties = getAssignmentDraftItemProperties(this.draftItemTypeLabel);

      return this.itemType.getPrefixTranslation({
        isGraded: draftItemProperties?.isGraded ?? this.typeName === 'staffGraded',
        isTeamAssignment:
          draftItemProperties?.isTeamSubmissionEnabled ??
          (this.content as StaffGradedContent)?.definition?.isTeamSubmissionEnabled,
      });
    }

    return this.itemType && this.itemType.getPrefixTranslation();
  }

  get isDraft() {
    return this.draftName !== undefined;
  }

  get isFrozen() {
    return !!this.atomId?.includes('@');
  }

  get isGraded() {
    return !!(this.itemType && this.itemType.graded);
  }

  /*
    Evaluable items are items that can be assigned grades. However, not all evaluable items count towards the grading
    formula or overall completion of a course. Only items that are evaluable and passable can count towards the grading
    formula or overall completion of a course. Examples of an evaluable item that is not passable is Practice Assignments.

    All Graded items e.g. Graded Quiz, Graded Assignments e.t.c, are evaluable and passable.
  */
  get isEvaluable() {
    return this.itemType?.graded || this.itemType?.typeName === 'ungradedAssignment';
  }

  get isEditable() {
    return !!(this.itemType && this.itemType.editable);
  }

  get isBranchPrivate() {
    return this.context.isBranchPrivate;
  }

  get isBreakingChangeAllowed() {
    return this.context.isBreakingChangeAllowed;
  }

  get hasLatePenalty() {
    return !!(this.definition as GradableStoredContent['definition']).gradingLatePenalty;
  }

  get gradingLatePenalty() {
    return this.hasLatePenalty ? (this.definition as GradableStoredContent['definition']).gradingLatePenalty : null;
  }

  get latePenaltyValue() {
    return this.hasLatePenalty && (this.gradingLatePenalty as LatePenalty).definition.penalty;
  }

  get isIncrementalLatePenalty() {
    return this.gradingLatePenalty?.typeName === INCREMENTAL;
  }

  get isFixedLatePenalty() {
    return this.gradingLatePenalty?.typeName === FIXED;
  }

  get isAssignment() {
    const { ASSIGNMENT, PRACTICE_ASSIGNMENT } = ItemTypes;
    // @ts-expect-error TS18048 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    const types = [ASSIGNMENT.typeName, PRACTICE_ASSIGNMENT.typeName];

    return types.includes(this.typeName);
  }

  get isQuiz(): boolean {
    const { FORMATIVE_QUIZ, SUMMATIVE_QUIZ } = ItemTypes;
    // @ts-expect-error TS18048 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    const types = [FORMATIVE_QUIZ.typeName, SUMMATIVE_QUIZ.typeName];

    return types.indexOf(this.typeName) !== -1;
  }

  get isGradedProgramming(): boolean {
    // @ts-expect-error TS18048 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    return this.typeName === ItemTypes.GRADED_PROGRAMMING.typeName;
  }

  get isUngradedProgramming(): boolean {
    // @ts-expect-error TS18048 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    return this.typeName === ItemTypes.UNGRADED_PROGRAMMING.typeName;
  }

  get isProgramming(): boolean {
    return (
      // @ts-expect-error TS18048 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      this.typeName === ItemTypes.GRADED_PROGRAMMING.typeName ||
      // @ts-expect-error TS18048 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      this.typeName === ItemTypes.UNGRADED_PROGRAMMING.typeName
    );
  }

  get isUngradedLab(): boolean {
    const { UNGRADED_LAB } = ItemTypes;
    // @ts-expect-error TS18048 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    return this.typeName === UNGRADED_LAB.typeName;
  }

  get isPeer(): boolean {
    const { CLOSED_PEER, GRADED_PEER, PHASED_PEER, STANDARD_PEER } = ItemTypes;
    // @ts-expect-error TS18048 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    const types = [CLOSED_PEER.typeName, GRADED_PEER.typeName, PHASED_PEER.typeName, STANDARD_PEER.typeName];

    return types.indexOf(this.typeName) !== -1;
  }

  get isLecture(): boolean {
    const { LECTURE } = ItemTypes;
    // @ts-expect-error TS18048 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    return this.typeName === LECTURE.typeName;
  }

  get isReading(): boolean {
    const { SUPPLEMENT } = ItemTypes;
    // @ts-expect-error TS18048 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    return this.typeName === SUPPLEMENT.typeName;
  }

  get isPlugin(): boolean {
    const { UNGRADED_PLUGIN, GRADED_PLUGIN } = ItemTypes;
    // @ts-expect-error TS18048 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    const types = [UNGRADED_PLUGIN.typeName, GRADED_PLUGIN.typeName];
    return types.indexOf(this.typeName) !== -1;
  }

  get isLti(): boolean {
    const { UNGRADED_LTI, GRADED_LTI } = ItemTypes;
    // @ts-expect-error TS18048 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    const types = [UNGRADED_LTI.typeName, GRADED_LTI.typeName];
    return types.indexOf(this.typeName) !== -1;
  }

  get isCoachItem() {
    return this.typeName === COACH_ITEM_SHARED_ITEM_TYPENAME;
  }

  get isTeammateReview() {
    const { TEAMMATE_REVIEW } = ItemTypes;
    // @ts-expect-error TS18048 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    return this.typeName === TEAMMATE_REVIEW.typeName;
  }

  get isStaffGradedProject() {
    const { STAFF_GRADED } = ItemTypes;
    // @ts-expect-error TS18048 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    return this.typeName === STAFF_GRADED.typeName;
  }

  get isIndividualStaffGradedProject() {
    return this.isStaffGradedProject && !(this.content as StaffGradedContent).definition.isTeamSubmissionEnabled;
  }

  get isTeamStaffGradedProject() {
    return this.isStaffGradedProject && (this.content as StaffGradedContent).definition.isTeamSubmissionEnabled;
  }

  get isGradedOrUngradedDiscussionPrompt() {
    const { GRADED_DISCUSSION_PROMPT, DISCUSSION_PROMPT } = ItemTypes;
    // @ts-expect-error TS18048 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    return this.typeName === GRADED_DISCUSSION_PROMPT.typeName || this.typeName === DISCUSSION_PROMPT.typeName;
  }

  get isGradedDiscussionPrompt() {
    const { GRADED_DISCUSSION_PROMPT } = ItemTypes;
    // @ts-expect-error TS18048 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    return this.typeName === GRADED_DISCUSSION_PROMPT.typeName;
  }

  get isPlaceholder() {
    const { GRADED_PLACEHOLDER, UNGRADED_PLACEHOLDER } = ItemTypes;
    // @ts-expect-error TS18048 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    return this.typeName === GRADED_PLACEHOLDER.typeName || this.typeName === UNGRADED_PLACEHOLDER.typeName;
  }

  get isSubmissionBased() {
    // item types that require learners to submit something to complete it
    return !(this.isReading || this.isLecture || this.isPlugin);
  }

  get isAutoGraded() {
    return !(
      this.isStaffGradedProject ||
      this.isTeamStaffGradedProject ||
      this.isTeammateReview ||
      this.isGradedDiscussionPrompt
    );
  }

  get isAssessment() {
    const { assessmentTypes } = constants.items;
    return assessmentTypes.includes(this.typeName);
  }

  get defaultTimeCommitment(): number | undefined {
    // The default time estimate for quizzes is (2 mins * the # of questions)
    if (this.isQuiz) {
      const questionCount = (this.definition as QuizTypeContent['definition']).questionCount;
      return moment.duration(questionCount * 2, 'minutes').asMilliseconds();
    } else if (this.isLecture) {
      return (this.definition as LectureContent['definition']).duration;
    }

    if (Object.prototype.hasOwnProperty.call(DefaultTimeCommitments, this.typeName)) {
      return DefaultTimeCommitments[this.typeName as keyof typeof DefaultTimeCommitments];
    }

    // For any items not listed in the above DefaultTimeCommitments object, the estimate is 0.
    return 0;
  }

  checkIfUpdated(scope: ItemScope | ItemLockingScope) {
    const { typeName, definition } = scope;

    switch (typeName) {
      case ChangeScopeTypes.Item.LOCKS:
        return true;

      case ChangeScopeTypes.Item.ITEM:
        return definition.itemId === this.id;

      default:
        return false;
    }
  }

  getParentIdsForItems(itemIds: string[]) {
    const isItemInList = itemIds.find((itemId) => this.id === itemId);
    if (isItemInList) {
      const parentIds: {
        parentModuleId: string;
        parentLessonId: string;
      } = {
        parentModuleId: this.moduleId,
        parentLessonId: this.lessonId,
      };

      const parentIdsForItem = {
        [this.id]: parentIds,
      };

      return parentIdsForItem;
    }

    return {};
  }

  updateDrafts(drafts: Drafts) {
    const draft = drafts[this.id];

    if (!draft) {
      return;
    }

    if (this.draftName !== draft.draftName) {
      this.isUpdated = true;
      this.draftName = draft.draftName;
    }
    // update draft config if the item is an assignment
    if (
      this.typeName === 'staffGraded' ||
      this.typeName === 'ungradedAssignment' ||
      this.typeName === 'gradedWidget' ||
      this.typeName === 'ungradedWidget'
    ) {
      if (this.draftItemTypeLabel !== draft.itemTypeLabel) {
        this.isUpdated = true;
        this.draftItemTypeLabel = draft.itemTypeLabel;
      }
    }
  }
}

export default Item;
